'use client'

import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import {
  usePreloadCommunityProjects,
  usePreloadUserProjects,
} from '@/hooks/use-preload'
import type { User } from '@/lib/types'
import { TemplateList } from './template/template-list'
import { UserProjectList } from './user-and-community-projects'
import { AgentList } from './agent-list'

export function ProjectsV3({ user }: { user: User | null }) {
  const router = useRouter()
  const [activeTab, setActiveTab] = useState<string>('templates') // Always default to templates
  const [isTabLoading, setIsTabLoading] = useState(false)
  const searchParams = useSearchParams()
  const t = useTranslations('home.projects')

  // Load first page immediately
  usePreloadCommunityProjects(0, 8, 'all')

  // Preload templates immediately for faster loading
  useEffect(() => {
    // Preload first 8 templates for fast initial load
    const preloadTemplates = async () => {
      try {
        // After initial load, preload other categories in background
        setTimeout(() => {
          const categories = [
            'ai-agents',
            'apps',
            'blogs-contents',
            'e-commerce',
            'landing-pages',
            'marketing',
            'portfolio',
          ]
          categories.forEach(async (category, index) => {
            // Stagger the requests to avoid overwhelming the server
            setTimeout(async () => {
              try {
                await fetch(
                  `/api/templates?skip=0&limit=8&category=${encodeURIComponent(category)}`,
                )
              } catch (_error) {
                // console.warn(`Failed to preload category ${category}:`, error)
              }
            }, index * 200) // 200ms delay between each category
          })
        }, 1000) // Wait 1 second after initial load
      } catch (_error) {
        // console.warn('Failed to preload templates:', error)
      }
    }
    preloadTemplates()
  }, [])

  // Use state to control sequential preloading
  const [preloadPage1, setPreloadPage1] = useState(false)
  const [preloadPage2, setPreloadPage2] = useState(false)
  const [preloadUserProjects, setPreloadUserProjects] = useState(false)

  // Preload additional pages after a short delay
  useEffect(() => {
    const timer1 = setTimeout(() => setPreloadPage1(true), 100)
    const timer2 = setTimeout(() => setPreloadPage2(true), 200)
    const timer3 = setTimeout(() => setPreloadUserProjects(true), 300)

    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
    }
  }, [])

  // Conditionally call hooks based on state
  usePreloadCommunityProjects(1, 8, 'all', preloadPage1)
  usePreloadCommunityProjects(2, 8, 'all', preloadPage2)
  usePreloadUserProjects(0, 8, !!(user && preloadUserProjects))

  const handleTabChange = (value: string) => {
    if (value === activeTab) return
    // Agents tab is static, no loading needed
    if (value === 'agents') {
      setIsTabLoading(false)
    } else {
      // For other tabs, show loading briefly
      setIsTabLoading(true)
    }
    setActiveTab(value)
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('tab', value)
    router.push(`?${searchParams.toString()}`, { scroll: false })
  }

  useEffect(() => {
    // Get the "tab" query parameter from the URL, always default to templates
    let tab = searchParams.get('tab') || 'templates'

    // Validate tab value
    const validTabs = ['templates', 'agents', 'my-projects']
    if (!validTabs.includes(tab)) {
      tab = 'templates'
    }

    // If tab is "my-projects" and user is not logged in, redirect to "templates"
    if (!user && tab === 'my-projects') {
      tab = 'templates'
    }

    // Only update if tab actually changed using functional update
    setActiveTab((currentTab) => {
      if (tab !== currentTab) {
        // Agents tab is static, no loading needed
        if (tab === 'agents') {
          setIsTabLoading(false)
        } else {
          // For other tabs, set loading to true initially
          setIsTabLoading(true)
        }
        return tab
      }
      return currentTab
    })
  }, [searchParams, user]) // Remove activeTab from dependencies to avoid infinite loop

  return (
    <div className="w-full">
      <div className="w-[90vw] mx-auto py-20">
        {/* Toggle Header */}
        <div className="flex flex-col items-center justify-center gap-2 mb-16">
          <div className="flex items-center gap-6 md:gap-12 text-xl sm:text-2xl md:text-4xl font-bold tracking-tight">
            {/* Templates Tab */}
            <button
              type="button"
              onClick={() => {
                return handleTabChange('templates')
              }}
              className={`relative pb-2 transition-colors duration-300 ${
                activeTab === 'templates'
                  ? 'text-white'
                  : 'text-white/20 hover:text-white'
              }`}
            >
              {t('templates')}
              {activeTab === 'templates' && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gold-gradient rounded-full" />
              )}
            </button>

            {/* Agents Tab */}
            <button
              type="button"
              onClick={() => {
                return handleTabChange('agents')
              }}
              className={`relative pb-2 transition-colors duration-300 ${
                activeTab === 'agents'
                  ? 'text-white'
                  : 'text-white/20 hover:text-white'
              }`}
            >
              {t('agents')}
              {activeTab === 'agents' && (
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gold-gradient rounded-full" />
              )}
            </button>

            {/* My Projects Tab */}
            {user && (
              <button
                type="button"
                onClick={() => {
                  return handleTabChange('my-projects')
                }}
                className={`relative pb-2 transition-colors duration-300 ${
                  activeTab === 'my-projects'
                    ? 'text-white'
                    : 'text-white/20 hover:text-white'
                }`}
              >
                {t('my_projects')}
                {activeTab === 'my-projects' && (
                  <span className="absolute bottom-0 left-0 w-full h-1 bg-gold-gradient rounded-full" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Content Rendering */}
        <div className="relative min-h-[400px]">
          {activeTab === 'templates' && (
            <TemplateList
              user={user}
              onLoadingChange={(loading) => {
                // Only update loading state if templates tab is still active
                if (activeTab === 'templates') {
                  setIsTabLoading(loading)
                }
              }}
            />
          )}
          {activeTab === 'agents' && <AgentList />}
          {activeTab === 'my-projects' && (
            <UserProjectList
              transparent={true}
              showTitle={false}
              onLoadingChange={(loading) => {
                // Only update loading state if my-projects tab is still active
                if (activeTab === 'my-projects') {
                  setIsTabLoading(loading)
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
